import React, { useState } from "react";
import { Send, CheckCircle, AlertCircle } from "lucide-react";

export default function ContactForm({ projectType = "" }) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: projectType ? `Hej Henneberg Byggeri,\n\nJeg vil gerne have et uforpligtende tilbud på ${projectType.toLowerCase()}...` : "",
    website_url: "" // Honeypot felt
  });

  const [status, setStatus] = useState("idle"); // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    // Enkel validering
    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.description.trim()) {
      setStatus("error");
      setErrorMessage("Venligst udfyld alle påkrævede felter.");
      return;
    }

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          description: "",
          website_url: ""
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        setStatus("error");
        setErrorMessage(errData.message || "Der skete en fejl ved afsendelsen. Prøv venligst igen.");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus("error");
      setErrorMessage("Netværksfejl. Kontroller din internetforbindelse og prøv igen.");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-950/20 p-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400">
          <CheckCircle className="h-6 w-6" />
        </div>
        <h3 className="text-lg font-bold text-white mb-2">Tak for din henvendelse!</h3>
        <p className="text-sm text-gray-300 leading-relaxed">
          Vi har modtaget din besked og vender tilbage til dig hurtigst muligt – normalt inden for 24 timer på hverdage.
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="mt-6 text-sm font-semibold text-[#C2A679] hover:underline focus:outline-none"
        >
          Send en ny besked
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 rounded-lg border border-neutral-800 bg-[#1E2022] p-6 sm:p-8 shadow-lg">
      <h3 className="text-xl font-bold text-white mb-2">Få et uforpligtende tilbud</h3>
      <p className="text-sm text-gray-400 mb-6">
        Udfyld formularen nedenfor, så vender vi hurtigt tilbage med professionel rådgivning og et skarpt tilbud.
      </p>

      {/* Antispam Honeypot - absolut skjult for mennesker, men synlig for robotter */}
      <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
        <label htmlFor="website_url">Efterlad dette felt tomt</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={formData.website_url}
          onChange={handleChange}
          tabIndex="-1"
          autoComplete="off"
        />
      </div>

      {/* Navn */}
      <div>
        <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-gray-300 mb-1">
          Navn <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          required
          value={formData.name}
          onChange={handleChange}
          placeholder="Dit fulde navn"
          className="w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-neutral-700 bg-transparent px-0 py-2.5 text-sm text-white placeholder-gray-500 transition-colors focus:border-[#C2A679] focus:outline-none focus:ring-0"
        />
      </div>

      {/* Telefon & E-mail i grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-gray-300 mb-1">
            Telefonnummer <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            value={formData.phone}
            onChange={handleChange}
            placeholder="Tlf. nummer"
            className="w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-neutral-700 bg-transparent px-0 py-2.5 text-sm text-white placeholder-gray-500 transition-colors focus:border-[#C2A679] focus:outline-none focus:ring-0"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-gray-300 mb-1">
            E-mailadresse <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="din@email.dk"
            className="w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-neutral-700 bg-transparent px-0 py-2.5 text-sm text-white placeholder-gray-500 transition-colors focus:border-[#C2A679] focus:outline-none focus:ring-0"
          />
        </div>
      </div>

      {/* Beskrivelse */}
      <div>
        <label htmlFor="description" className="block text-xs font-bold uppercase tracking-wider text-gray-300 mb-1">
          Beskrivelse af opgaven <span className="text-red-500">*</span>
        </label>
        <textarea
          id="description"
          name="description"
          required
          rows="4"
          value={formData.description}
          onChange={handleChange}
          placeholder="Beskriv dit projekt, dine ønsker og eventuelle tidsrammer..."
          className="w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-neutral-700 bg-transparent px-0 py-2.5 text-sm text-white placeholder-gray-500 transition-colors focus:border-[#C2A679] focus:outline-none focus:ring-0 resize-y"
        ></textarea>
      </div>

      {/* Fejlbesked */}
      {status === "error" && (
        <div className="flex items-start gap-2.5 rounded border border-red-500/30 bg-red-950/20 p-3.5 text-sm text-red-400">
          <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Submit-knap */}
      <button
        type="submit"
        disabled={status === "submitting"}
        className="flex w-full items-center justify-center gap-2 rounded bg-[#434359] py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-[#343446] disabled:opacity-50 active:scale-[0.98]"
      >
        {status === "submitting" ? (
          <>
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
            Sender...
          </>
        ) : (
          <>
            <Send className="h-4 w-4" />
            Send henvendelse
          </>
        )}
      </button>
    </form>
  );
}
